// reports/ReopenComplaintsReport.jsx
//
// A listing of every Re-Opened complaint. Each complaint is a BLOCK:
//   Row 1 : Complaint No. | Complainant | Referred By   (three columns)
//   Row 2 : "Complaint Detail"  — merged across the three columns above
//   Row 3 : "DCF Remarks"       — merged across the three columns above
// A single tall "Remarks (Write by Hand)" column sits on the right and spans
// the full height of the block, giving one large blank area to write in.
//
// Mirror of ClosedComplaintsReport.jsx (only the titles differ).

import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import './pdfFonts';

const SKY = { 50: '#f0f9ff', 100: '#e0f2fe', 200: '#bae6fd', 800: '#075985', 900: '#0c4a6e' };

// Left block vs. handwriting column (must sum to 100%).
const LEFT = '64%';
const HAND = '36%';
// Widths of the three top cells, as a share of the LEFT block.
const TOP = { number: '30%', complainant: '38%', referred: '32%' };

const styles = StyleSheet.create({
  page: {
    fontFamily: 'NotoNaskhArabic',
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    paddingTop: 30,
    paddingBottom: 50,
    paddingLeft: 20,
    paddingRight: 20,
  },
  headerBlock: { minHeight: 90 },
  dividerTop: { marginBottom: 20, borderBottomWidth: 1, borderBottomColor: '#ccc', marginVertical: 10 },
  heading: { fontSize: 20, marginBottom: 0, marginTop: 10, textAlign: 'center', fontWeight: 'bold', color: SKY[800] },
  subHeading: { fontSize: 11, textAlign: 'center', marginTop: 4, marginBottom: 12, color: '#555', fontWeight: 'bold' },

  table: { width: '100%', borderWidth: 1, borderColor: SKY[200], borderRadius: 8, marginTop: 6, marginBottom: 10, overflow: 'hidden' },

  headerRow: { flexDirection: 'row', backgroundColor: SKY[100], borderBottomWidth: 1, borderBottomColor: SKY[200] },
  headerLeft: { width: LEFT, flexDirection: 'row' },
  cellHeader: {
    paddingVertical: 7, paddingHorizontal: 5, fontSize: 8.5, fontWeight: 'bold', color: SKY[800],
    borderRightWidth: 1, borderRightColor: SKY[200],
  },
  handHeader: {
    width: HAND, paddingVertical: 7, paddingHorizontal: 5, fontSize: 8.5, fontWeight: 'bold',
    color: SKY[800], textAlign: 'center',
  },

  block: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: SKY[200] },
  blockLeft: { width: LEFT, flexDirection: 'column' },
  topRow: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: SKY[100] },
  topCell: {
    paddingVertical: 6, paddingHorizontal: 5, fontSize: 8.5, color: '#333',
    borderRightWidth: 1, borderRightColor: SKY[100],
  },
  numberText: { fontSize: 8.5, color: '#333', fontWeight: 'bold' },

  mergedRow: { paddingVertical: 5, paddingHorizontal: 6, borderBottomWidth: 1, borderBottomColor: SKY[100] },
  mergedRowLast: { borderBottomWidth: 0 },
  mergedLabel: {
    fontSize: 7, fontWeight: 'bold', color: SKY[800], textTransform: 'uppercase',
    letterSpacing: 0.4, marginBottom: 2,
  },
  mergedText: { fontSize: 8.5, color: '#333', lineHeight: 1.4 },

  handCell: { width: HAND, borderLeftWidth: 1, borderLeftColor: SKY[200], minHeight: 74 },

  emptyState: { marginTop: 40, textAlign: 'center', fontSize: 12, color: '#999', fontStyle: 'italic' },
});

const formatTitleDate = (date) => {
  const d = date ? new Date(date) : new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
};

const formatGeneratedAt = (date) =>
  (date ? new Date(date) : new Date()).toLocaleString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  });

const val = (v) => (v == null || v === '' ? 'N/A' : String(v));

const ReopenComplaintsReport = ({ reportData = [], generatedAt, qrCodeUrl, rangeLabel = '' }) => {
  const rows = Array.isArray(reportData) ? reportData : [];
  const total = rows.length;

  return (
    <Document
      title={`Re-Open Complaints List ${formatTitleDate(generatedAt)}`}
      author="Forest & Wildlife Department, Punjab"
      subject="Re-Open Complaints List"
    >
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.headerBlock} fixed>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 0 }}>
            <Image src={process.env.PUBLIC_URL + '/assets/images/logo_fw.png'} style={{ width: 130, height: 'auto' }} />
            <View style={{ flex: 0.5, alignItems: 'flex-end', justifyContent: 'flex-start' }}>
              {qrCodeUrl && <Image src={qrCodeUrl} style={{ width: 60, height: 60 }} />}
              <Text style={{ fontSize: 8, fontWeight: 'bold', marginTop: 2 }}>Re-Open Complaints List</Text>
              <Text style={{ fontSize: 8, textAlign: 'right', marginBottom: 0, marginTop: 5, fontWeight: 'bold' }}>
                Generated Date: {formatGeneratedAt(generatedAt)}
              </Text>
            </View>
          </View>
          <View style={styles.dividerTop} />
        </View>

        <Text style={styles.heading}>Re-Open Complaints List {formatTitleDate(generatedAt)}</Text>
        <Text style={styles.subHeading}>
          {total} re-opened complaint{total === 1 ? '' : 's'}{rangeLabel ? ` · ${rangeLabel}` : ''}
        </Text>

        {rows.length === 0 ? (
          <Text style={styles.emptyState}>No re-opened complaints found at the time this report was generated.</Text>
        ) : (
          <View style={styles.table}>
            <View style={styles.headerRow} fixed>
              <View style={styles.headerLeft}>
                <Text style={[styles.cellHeader, { width: TOP.number }]}>Complaint No.</Text>
                <Text style={[styles.cellHeader, { width: TOP.complainant }]}>Complainant</Text>
                <Text style={[styles.cellHeader, { width: TOP.referred, borderRightWidth: 0 }]}>Referred By</Text>
              </View>
              <Text style={styles.handHeader}>Remarks</Text>
            </View>

            {rows.map((c, index) => (
              <View key={`${c.complaint_number || 'row'}-${index}`} style={styles.block} wrap={false}>
                <View style={styles.blockLeft}>
                  <View style={styles.topRow}>
                    <View style={[styles.topCell, { width: TOP.number }]}>
                      <Text style={styles.numberText}>{index + 1}. {val(c.complaint_number)}</Text>
                    </View>
                    <View style={[styles.topCell, { width: TOP.complainant }]}>
                      <Text>{val(c.complainant_name)}</Text>
                    </View>
                    <View style={[styles.topCell, { width: TOP.referred, borderRightWidth: 0 }]}>
                      <Text>{val(c.complaint_reffered_by)}</Text>
                    </View>
                  </View>

                  <View style={styles.mergedRow}>
                    <Text style={styles.mergedLabel}>Complaint Detail</Text>
                    <Text style={styles.mergedText}>{val(c.complaint_detail)}</Text>
                  </View>

                  <View style={styles.mergedRow}>
                    <Text style={styles.mergedLabel}>Closing Remarks</Text>
                    <Text style={styles.mergedText}>{val(c.dfo_remarks)}</Text>
                  </View>

                  <View style={[styles.mergedRow, styles.mergedRowLast]}>
                    <Text style={styles.mergedLabel}>Re-Open Remarks</Text>
                    <Text style={styles.mergedText}>{val(c.reopen_remarks)}</Text>
                  </View>
                </View>

                <View style={styles.handCell}>
                  <Text> </Text>
                </View>
              </View>
            ))}
          </View>
        )}

        <View
          style={{ position: 'absolute', bottom: 10, left: 20, right: 20, flexDirection: 'column', alignItems: 'center' }}
          fixed
        >
          <View style={{ width: '100%', height: 1, backgroundColor: 'black', marginBottom: 5, marginTop: 10 }} />
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', width: '100%', fontSize: 6 }}>
            <Text>F&W Department, Punjab, Pakistan</Text>
            <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default ReopenComplaintsReport;
